import {
  checkArgument,
  checkExists,
  checkState,
} from 'external/dev_april_corgi+/js/common/asserts';
import {DefaultMap} from 'external/dev_april_corgi+/js/common/collections';
import {approxGtOrEqual} from 'external/dev_april_corgi+/js/common/comparisons';
import {Future, asFuture} from 'external/dev_april_corgi+/js/common/futures';
import {waitMs} from 'external/dev_april_corgi+/js/common/promises';

import {
  Action,
  ActionFor,
  ActionResultMap,
  ActionToResponse,
  LoadPlateAction,
  PipetteAction,
  Result,
  ResultFor,
} from '../types/actions';
import {DataValue, PlateId, MaterialValue} from '../types/data';
import {PlateState, WellState} from '../types/lab';

import {ActionsHandler} from './actions_handler';
import {PipeId} from './graph';
import {Sink, Source} from './pipes';

type Pipe = Source<unknown> | Sink<unknown>;

export class ActionsBuffer {
  private readonly actions: Array<ActionToResponse>;

  constructor() {
    this.actions = [];
  }

  get(): Array<ActionToResponse> {
    const actions = [...this.actions];
    this.actions.length = 0;
    return actions;
  }

  evaporate(
    at: PlateId,
    {
      minutes,
      seconds,
    }: {
      minutes?: number;
      seconds?: number;
    },
  ): Promise<ResultFor<'evaporate'>> {
    return this.pushAction('empty', {
      kind: 'evaporate',
      at,
      durationUs: ((minutes ?? 0) * 60 + (seconds ?? 0)) * 1000 * 1000,
    });
  }

  loadMaterial(
    material: MaterialValue,
    well: WellState,
    volumeNl: number,
  ): Promise<ResultFor<'load_material'>> {
    well.load(volumeNl);
    return this.pushAction('load_material', {
      kind: 'load_material',
      into: well.location,
      material,
      volumeNl,
    });
  }

  loadPlate(
    plate: PlateId,
    capabilities: LoadPlateAction['capabilities'],
  ): Promise<ResultFor<'load_plate'>> {
    return this.pushAction('load_plate', {
      kind: 'load_plate',
      id: plate,
      capabilities,
    });
  }

  magnetBlock(
    plate: PlateState,
    {on}: {on: boolean},
  ): Promise<ResultFor<'magnet_block'>> {
    return this.pushAction('empty', {
      kind: 'magnet_block',
      at: plate.id,
      on,
    });
  }

  thermocycleBlockDeactivate(
    plate: PlateState,
  ): Promise<ResultFor<'thermocycle_block_deactivate'>> {
    return this.pushAction('empty', {
      kind: 'thermocycle_block_deactivate',
      at: plate.id,
    });
  }

  thermocycleBlockTemperature(
    plate: PlateState,
    {
      holdDurationUs,
      temperatureC,
    }: {
      holdDurationUs: number | undefined;
      temperatureC: number;
    },
  ): Promise<ResultFor<'thermocycle_block_temperature'>> {
    return this.pushAction('empty', {
      kind: 'thermocycle_block_temperature',
      at: plate.id,
      durationUs: holdDurationUs,
      // TODO(april): how can we check this is below the maximum?
      maxVolumeNl: plate.getCurrentVolumeNl(),
      temperatureC,
    });
  }

  thermocycleLidDeactivate(
    plate: PlateState,
  ): Promise<ResultFor<'thermocycle_lid_deactivate'>> {
    return this.pushAction('empty', {
      kind: 'thermocycle_lid_deactivate',
      at: plate.id,
    });
  }

  thermocycleLidHinge(
    plate: PlateState,
    closed: boolean,
  ): Promise<ResultFor<'thermocycle_lid_hinge'>> {
    return this.pushAction('empty', {
      kind: 'thermocycle_lid_hinge',
      at: plate.id,
      closed,
    });
  }

  thermocycleLidTemperature(
    plate: PlateState,
    temperatureC: number,
  ): Promise<ResultFor<'thermocycle_lid_temperature'>> {
    return this.pushAction('empty', {
      kind: 'thermocycle_lid_temperature',
      at: plate.id,
      temperatureC,
    });
  }

  storeMaterial(
    material: MaterialValue,
    well: WellState,
    volumeNl: number,
  ): Promise<ResultFor<'store_material'>> {
    well.remove(volumeNl);
    return this.pushAction('store_material', {
      kind: 'store_material',
      from: well.location,
      material,
      volumeNl,
    });
  }

  async pipette(
    source: WellState,
    destination: WellState,
    {
      mix,
      sourceContaminationAllowed,
      volumeNl,
    }: {
      mix: boolean;
      sourceContaminationAllowed?: boolean;
      volumeNl: number;
    },
  ): Promise<ResultFor<'pipette'>> {
    if (volumeNl === 0) {
      // Resolve when the last action's promise resolves, or just resolve now.
      const last = this.actions[this.actions.length - 1]?.promise;
      if (last) {
        await last;
      }
      return {kind: 'empty'};
    } else if (!approxGtOrEqual(volumeNl, 1000, /* epsilon= */ 1 /* nl */)) {
      // TODO(april): how do we do this check properly?
      throw new Error(`Volume ${volumeNl}nl is below 1ul, will fail`);
    }

    // TODO(april): not necessarily true, we could compare the materials, but yolo
    // We check the maximum volume as a way of saying was there *ever* anything in this well
    const destinationContaminatesTip = destination.getHighVolumeNl() > 0;
    source.remove(volumeNl);
    destination.add(volumeNl);
    const last = this.actions[this.actions.length - 1];
    let pipette: PipetteAction;
    let response: Promise<ResultFor<'pipette'>>;
    if (last?.action?.kind === 'pipette') {
      pipette = last.action;
      response = last.promise as Promise<ResultFor<'pipette'>>;
    } else {
      pipette = {
        kind: 'pipette',
        movements: [],
      };
      response = this.pushAction('empty', pipette);
    }
    pipette.movements.push({
      from: source.location,
      to: destination.location,
      destinationContaminatesTip,
      // If the user moves some liquid into an empty well it doesn't really make sense to mix.
      // Originally I called this a sketchy optimization and said it makes more sense to only do
      // this if there is liquid. But actually from a bead wash perspective we probably want to do
      // a mix even if the well is *currently* volume-less. So whatever, let's just keep it.
      mix: mix && destinationContaminatesTip,
      sourceContaminationAllowed: sourceContaminationAllowed ?? false,
      volumeNl,
    });
    return response;
  }

  wait({
    minutes,
    seconds,
  }: {
    minutes?: number;
    seconds?: number;
  }): Promise<ResultFor<'wait'>> {
    return this.pushAction('empty', {
      kind: 'wait',
      durationUs: ((minutes ?? 0) * 60 + (seconds ?? 0)) * 1000 * 1000,
    });
  }

  private pushAction<
    K extends Action['kind'],
    R extends ActionResultMap[K]['result'],
  >(responseKind: R['kind'], action: ActionFor<K>): Promise<R> {
    const capture = {
      resolve: undefined as ((v: Result) => void) | undefined,
      reject: undefined as ((reason: unknown) => void) | undefined,
    };
    const promise = new Promise<R>((resolve, reject) => {
      capture.resolve = (v: Result) => {
        if (v.kind === responseKind) {
          resolve(v as R);
        } else {
          reject('Wrong kind');
        }
      };
      capture.reject = reject;
    });
    this.actions.push({
      action,
      promise,
      resolve: checkExists(capture.resolve),
      reject: checkExists(capture.reject),
    });
    return promise;
  }
}

export class ActionsDispatcher {
  private readonly actors: DefaultMap<Pipe, ActionsBuffer[]>;
  private readonly blockedBy: Map<Pipe, Set<Pipe>>;
  private readonly closed: Set<Pipe>;
  private readonly finished: Set<Pipe>;
  private readonly worker: Future<void>;
  private initializing: boolean;
  private _failed: unknown;
  private openPipes: number;

  constructor(
    private readonly handler: ActionsHandler,
    private readonly pollMs: number = 1,
  ) {
    this.actors = new DefaultMap(() => []);
    this.blockedBy = new Map();
    this.closed = new Set();
    this.finished = new Set();
    this.worker = asFuture(this.run());
    this.initializing = true;
    this._failed = undefined;
    this.openPipes = 0;
  }

  initialized(): void {
    this.initializing = false;
  }

  failed(reason: unknown): void {
    if (!this._failed) {
      this._failed = reason;
    }
  }

  pipeBlockedOn(pipe: Pipe, on: Pipe[]): void {
    checkState(
      !this.blockedBy.has(pipe),
      `pipeBlockedOn has already been called for ${pipe.id.node}-${pipe.id.id}`,
    );
    this.blockedBy.set(pipe, new Set(on));
    this.openPipes += 1;
  }

  pipeFinished(pipe: Pipe): void {
    // Our dependency tracking requires a group for every pipe, so just make an empty one
    if (!this.actors.has(pipe)) {
      this.actor(pipe);
    }
    this.openPipes -= 1;
    this.closed.add(pipe);
  }

  actor(pipe: Pipe): ActionsBuffer {
    // If the graph has already failed, who cares
    if (this._failed) {
      return new ActionsBuffer();
    }

    const actor = new ActionsBuffer();
    this.actors.get(pipe).push(actor);
    return actor;
  }

  async waitForFinish(): Promise<void> {
    return this.worker;
  }

  private async run(): Promise<void> {
    await this.handler.start();
    const active = new Map<ActionsBuffer, Future<void>>();
    const finishes = new Map<PipeId, Future<void>>();
    while (!this._failed) {
      // Doing this allows us to fail early, or at the very least not hang forever
      for (const [actor, future] of [...active]) {
        if (future.finished) {
          active.delete(actor);
          if (!future.ok) {
            console.error('Pipe failed:', future.error());
            await future;
          }
        }
      }
      // Catch up on finished pipe notifications
      for (const [pipe, future] of [...finishes]) {
        if (future.finished) {
          finishes.delete(pipe);
          if (!future.ok) {
            console.error('Pipe finish failed:', future.error());
            await future;
          }
        }
      }

      let pipe = undefined;
      let actors = undefined;
      let considerFinish = false;
      for (const [key, buffers] of this.actors) {
        // Is there already something happening? Do nothing
        const inactive = buffers.filter(b => !active.has(b));
        if (inactive.length === 0) {
          continue;
        }

        const blockers = checkExists(this.blockedBy.get(key));
        let ready = true;
        for (const blocker of blockers) {
          if (!this.finished.has(blocker)) {
            ready = false;
            break;
          }
        }
        if (ready) {
          pipe = key;
          actors = inactive;
          considerFinish = buffers.length === inactive.length;
          break;
        }
      }

      if (pipe && actors) {
        const all = [];
        for (const actor of actors) {
          const actionsToResponse = actor.get();
          if (actionsToResponse.length === 0) {
            continue;
          }

          const future = asFuture(
            this.handler
              .execute(
                pipe.id,
                actionsToResponse.map(({action}) => action),
              )
              .then(
                results => {
                  checkArgument(
                    actionsToResponse.length === results.length,
                    'Expected equal numbers of actions and results',
                  );
                  for (let i = 0; i < actionsToResponse.length; ++i) {
                    actionsToResponse[i].resolve(results[i]);
                  }
                },
                reason => {
                  for (const {reject} of actionsToResponse) {
                    reject(reason);
                  }
                  throw reason;
                },
              ),
          );
          active.set(actor, future);
          all.push(future);
        }

        if (this.closed.has(pipe) && considerFinish && all.length === 0) {
          this.actors.delete(pipe);
          this.finished.add(pipe);
          finishes.set(
            pipe.id,
            asFuture(
              this.handler.pipeFinished(
                pipe.id,
                'lastValue' in pipe
                  ? (pipe.lastValue() as DataValue | undefined)
                  : undefined,
              ),
            ),
          );
        } else if (all.length === 0) {
          // Force a yield here, we didn't do any work this iteration
          await waitMs(this.pollMs);
        }
      } else if (
        !this.initializing &&
        this.openPipes === 0 &&
        active.size === 0
      ) {
        break;
      } else {
        await waitMs(this.pollMs);
      }
    }

    if (this._failed) {
      throw this._failed;
    } else {
      await Promise.all(active);
      await this.handler.finish();
    }
  }
}
